import { FC, ReactNode, useContext, useState } from 'react';
import {
  Dropdown,
  Button,
  Grid,
  Modal,
  Typography,
  Tooltip,
  theme,
  Popconfirm,
  type MenuProps,
} from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import Icon from '../../../components/atoms/Icon';
import { formActions, formStore } from '../../../store';
import { MessageContext } from '../../../providers/MessageContext';
import {
  FormPropsUIDescription,
  formStateDescription,
  workflowMenuSectionLabels,
  type FormStateId,
} from '#types';
import { createFormVersion } from '../../../services/artifactFunctions';
import { useFormWorkflowAction } from '../../../providers/artifacts/FormWorkflowActionContext';
import { isCloseAndCreateTargetState } from '../../../use/form/isCloseAndCreateTargetState';
import { isDraftFormState } from '../../../use/form/isDraftFormState';
import { draftFormStateId } from '../../../use/form/draftFormStateId';
import SelectCurrentVersion from './SelectCurrentVersion';
import { FormDerivedValuesDrawer } from './FormDerivedValuesDrawer';
import { UserContext } from '../../../providers/UserContext';
import { formDeriveCopy } from '@/services/formDerives';
import { useNavigate } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { checkRequiredFields } from '../../../use/form/checkRequiredFields';
import { useFormXlsxExport } from '@/components/ArTable/export';
import type { WorkflowMenuSection } from '#types';

/** Props for FormWorkflowSelect component */
interface FormWorkflowSelectProps {
  /** ID of the form being managed */
  formId: string;
  /** Restrict the dropdown to a single menu section. Omitted → the full dropdown. */
  section?: WorkflowMenuSection;
  /** Render one specific menu item (by key) as a direct action button instead of a dropdown. */
  itemKey?: string;
  /** Overrides the trigger/button label. */
  label?: string;
  /** Gate a single-item action behind a Popconfirm. */
  requiresConfirmation?: boolean;
  /** Disables the control (e.g. the form is read-only). */
  disabled?: boolean;
}

const { Text } = Typography;

/** A single Workflow menu entry, group, or divider, as consumed by the Dropdown. */
type WorkflowMenuItem = {
  key: string;
  label?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  danger?: boolean;
  type?: 'group' | 'divider';
  children?: WorkflowMenuItem[];
};

/** Plain-text labels for the fixed leaf items, shared by the menu and the button trigger. */
const staticItemLabels: Record<string, string> = {
  email: 'Send via Email',
  pdf: 'Export as PDF',
  exportTable: 'Export Tables to XLSX',
  'goto-current-version': 'Go to Current Version',
  'derived-values': formDeriveCopy.perFormMenuEntry,
};

/**
 * Dropdown component for managing form workflow actions including:
 * - Creating new versions
 * - Creating new forms
 * - Changing form states
 * - Exporting to PDF
 * - Sending via email
 *
 * The workflow, state-change, export, email, and close-&-create actions are owned
 * by FormWorkflowActionProvider (shared with the right-click menu and the command
 * palette); this component owns the local archive, create-version and
 * xlsx-export actions. The "Create new form" entries call `onCreateNextForm`,
 * which closes an open source version through the shared Close & Create drawer
 * before the next form exists (SYD-2816).
 *
 * The "Derived values…" entry opens `FormDerivedValuesDrawer` and is gated on the
 * right the Forms settings pane uses (`formsAdmin` or `orgsAdmin`), so it exists
 * for nobody else — the drawer states server-owned diagnostics, not form content.
 *
 * Reads the store through a narrow snapshot — ids, form number, form-type labels
 * — never `useFormStore()`, whose spread of `formVersion.data` would re-render
 * this control on every keystroke in the form.
 *
 * The menu is 320px wide, capped to the viewport less `marginLG` per side. Below
 * `md` it opens centred under the trigger; antd shifts a centred placement
 * sideways into the viewport, so a phone toolbar trigger never pushes it past
 * the right edge. From `md` it keeps the default `bottomLeft`.
 */
const FormWorkflowSelect: FC<FormWorkflowSelectProps> = ({
  formId,
  section,
  itemKey,
  label,
  requiresConfirmation,
  disabled = false,
}) => {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { md } = Grid.useBreakpoint();
  const { messageApi } = useContext(MessageContext);
  const snap = useSnapshot(formStore.state);
  const versionId = snap.versionId;
  const formNumber = snap.form.data?.formNumber;
  const currentVersionId = snap.form.data?.currentVersionId;
  const formTypeDescription = (formTypeId: string | undefined) =>
    formTypeId
      ? snap.formTypes.docs.get(formTypeId)?.data?.description
      : undefined;
  const { handleExportFormXlsx } = useFormXlsxExport();
  const { userRights } = useContext(UserContext);
  /** The right the Forms settings pane gates on — the diagnostics entry adds none. */
  const hasFormsAccess = !!(userRights.formsAdmin || userRights.orgsAdmin);
  const [derivedValuesOpen, setDerivedValuesOpen] = useState(false);

  const {
    isCreating,
    setIsCreating,
    allowedNextStates,
    allowedNextForms,
    onOpenCloseAndCreate,
    isCloseAndCreateEntryDisabled,
    onCreateNextForm,
    isCreateNextFormDisabled,
    applicableWorkflows,
    handleWorkflowClick,
    handleSetState,
    handleExport,
    handleEmailClick,
  } = useFormWorkflowAction();
  const [showArchiveModal, setShowArchiveModal] = useState(false);

  const handleExportTablesToXlsx = () => {
    if (!checkRequiredFields()) return;
    void handleExportFormXlsx();
  };

  /** Creates a new version of the current form */
  const handleCreateVersion = async (state: FormStateId) => {
    if (!versionId) return;
    if (!checkRequiredFields()) return;

    setIsCreating(true);
    try {
      const newVersionId = await createFormVersion(formId, versionId, state);
      formActions.navigateToVersion(formId, newVersionId);
    } catch (err) {
      messageApi.error(
        `Error creating new version: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setIsCreating(false);
    }
  };

  /** Opens archive modal */
  const handleArchiveClick = () => {
    setShowArchiveModal(true);
  };

  /** Handles archive confirmation */
  const handleArchiveConfirm = async () => {
    setShowArchiveModal(false);

    const archivedFormNumber = formNumber || '-';

    navigate('/forms');

    setTimeout(async () => {
      const archived = await formActions.archiveForm(formId);

      if (archived) {
        messageApi.success(`Form ${archivedFormNumber} archived successfully`);
      } else {
        messageApi.error(`Failed to archive form ${archivedFormNumber}`);
      }
    }, 100);
  };

  const noStateAllowedItem = [
    {
      key: 'no-states',
      label: 'Not allowed',
      disabled: true,
    },
  ];

  const noFormAllowedItem = [
    {
      key: 'no-forms',
      label: 'Not allowed',
      disabled: true,
    },
  ];

  const noDraftAllowedItem = [
    {
      key: 'no-draft',
      label: 'Not allowed',
      disabled: true,
    },
  ];

  const createItems: WorkflowMenuItem[] = [
    {
      key: 'share',
      type: 'group' as const,
      label: workflowMenuSectionLabels.share,
      children: [
        {
          key: 'email',
          label: <span>{staticItemLabels.email}</span>,
          onClick: handleEmailClick,
        },
        {
          key: 'pdf',
          label: <span>{staticItemLabels.pdf}</span>,
          onClick: handleExport,
        },
        {
          key: 'exportTable',
          label: staticItemLabels.exportTable,
          onClick: handleExportTablesToXlsx,
        },
      ],
    },
    ...(applicableWorkflows.length > 0
      ? [
          {
            key: 'divider-workflows',
            type: 'divider' as const,
          },
          {
            key: 'workflows',
            type: 'group' as const,
            label: workflowMenuSectionLabels.workflows,
            children: applicableWorkflows.map((w) => ({
              key: `workflow-${w.docId}`,
              label: (
                <Tooltip
                  title={w.description?.longDescription}
                  placement="left"
                >
                  {w.description?.shortLabel || w.docId}
                </Tooltip>
              ),
              onClick: () => {
                void handleWorkflowClick(w);
              },
              disabled: isCreating,
            })),
          },
        ]
      : []),
    {
      key: 'divider1',
      type: 'divider' as const,
    },
    {
      key: 'close-create',
      label: 'Close & Create',
      onClick: onOpenCloseAndCreate,
      disabled: isCloseAndCreateEntryDisabled,
    },
    {
      key: 'divider-close-create',
      type: 'divider' as const,
    },
    {
      key: 'state',
      type: 'group' as const,
      label: workflowMenuSectionLabels.state,
      children:
        (allowedNextStates?.length ?? 0) > 0
          ? (allowedNextStates
              ?.filter(isCloseAndCreateTargetState)
              .map((state: FormStateId) => ({
                key: `state-${state}`,
                label: (
                  <Tooltip
                    title={formStateDescription[state]?.longDescription}
                    placement="left"
                  >
                    {formStateDescription[state]?.shortLabel || state}
                  </Tooltip>
                ),
                onClick: () => handleSetState(state),
              })) ?? [])
          : noStateAllowedItem,
    },
    {
      key: 'divider2',
      type: 'divider' as const,
    },
    {
      key: 'form',
      type: 'group' as const,
      label: workflowMenuSectionLabels.form,
      children:
        (allowedNextForms?.length ?? 0) > 0
          ? (allowedNextForms?.map((formType) => ({
              key: `form-${formType}`,
              label: (
                <Tooltip
                  title={formTypeDescription(formType)?.longDescription}
                  placement="left"
                >
                  {formTypeDescription(formType)?.shortLabel || formType}
                </Tooltip>
              ),
              onClick: () => onCreateNextForm(formType),
              disabled: isCreateNextFormDisabled,
            })) ?? [])
          : noFormAllowedItem,
    },
    {
      key: 'divider3',
      type: 'divider' as const,
    },
    {
      key: 'version',
      type: 'group' as const,
      label: workflowMenuSectionLabels.version,
      children: [
        ...(allowedNextStates?.some(isDraftFormState)
          ? [
              {
                key: `state-draft`,
                label: (
                  <Tooltip
                    title={
                      formStateDescription[draftFormStateId]?.longDescription
                    }
                    placement="left"
                  >
                    New{' '}
                    {formStateDescription[draftFormStateId]?.shortLabel ||
                      'draft'}{' '}
                    {FormPropsUIDescription.formVersionNumber.shortLabel}
                  </Tooltip>
                ),
                onClick: () => handleCreateVersion(draftFormStateId),
              },
            ]
          : noDraftAllowedItem),
        {
          key: 'goto-current-version',
          label: (
            <Tooltip
              title="Navigate to the current version of this form"
              placement="left"
            >
              <Text
                disabled={!currentVersionId || versionId === currentVersionId}
              >
                {staticItemLabels['goto-current-version']}
              </Text>
            </Tooltip>
          ),
          onClick: () => {
            if (currentVersionId) {
              formActions.navigateToVersion(formId, currentVersionId);
            }
          },
          disabled: !currentVersionId || versionId === currentVersionId,
        },
      ],
    },
    {
      key: 'set-current-version',
      label: <SelectCurrentVersion disabled={isCreating} />,
      disabled: true,
    },
    ...(hasFormsAccess
      ? [
          {
            key: 'divider-derived-values',
            type: 'divider' as const,
          },
          {
            key: 'derived-values',
            label: staticItemLabels['derived-values'],
            onClick: () => setDerivedValuesOpen(true),
          },
        ]
      : []),
    {
      key: 'divider-archive',
      type: 'divider' as const,
    },
    {
      key: 'archive',
      label: 'Archive Form',
      danger: true,
      onClick: handleArchiveClick,
    },
  ];

  const leafItems: WorkflowMenuItem[] = createItems.flatMap(
    (item) => item.children ?? [item],
  );
  const targetedItem = itemKey
    ? leafItems.find((i) => i.key === itemKey)
    : undefined;
  const sectionGroup = section
    ? createItems.find((i) => i.key === section)
    : undefined;
  const dropdownItems: WorkflowMenuItem[] = section
    ? sectionGroup
      ? [sectionGroup]
      : [{ key: 'section-empty', label: 'Not available', disabled: true }]
    : createItems;

  const deriveItemLabel = (key: string): string => {
    if (key.startsWith('workflow-')) {
      return (
        applicableWorkflows.find((w) => `workflow-${w.docId}` === key)
          ?.description?.shortLabel ?? 'Workflow'
      );
    }
    if (key.startsWith('form-')) {
      const ftId = key.slice('form-'.length);
      return formTypeDescription(ftId)?.shortLabel ?? ftId;
    }
    if (key.startsWith('state-')) {
      const stateId = key.slice('state-'.length) as FormStateId;
      return formStateDescription[stateId]?.shortLabel ?? stateId;
    }
    return staticItemLabels[key] ?? key;
  };

  const triggerLabel =
    label ||
    (itemKey
      ? deriveItemLabel(itemKey)
      : section
        ? workflowMenuSectionLabels[section]
        : 'Workflow');
  const isDisabled = isCreating || disabled;

  const singleButton = (
    <Button
      type="primary"
      icon={<Icon.Zap />}
      loading={isCreating}
      disabled={isDisabled || !targetedItem || targetedItem.disabled}
      aria-label={`Run ${triggerLabel}`}
      onClick={
        requiresConfirmation ? undefined : () => targetedItem?.onClick?.()
      }
    >
      {triggerLabel}
    </Button>
  );

  const triggerControl = itemKey ? (
    requiresConfirmation && targetedItem && !isDisabled ? (
      <Popconfirm
        title={`Run ${triggerLabel}?`}
        okText="Run"
        cancelText="Cancel"
        onConfirm={() => targetedItem.onClick?.()}
      >
        {singleButton}
      </Popconfirm>
    ) : (
      singleButton
    )
  ) : (
    <Dropdown
      menu={{ items: dropdownItems as MenuProps['items'] }}
      trigger={['click']}
      disabled={isDisabled}
      placement={md ? undefined : 'bottom'}
      styles={{
        root: { width: 320, maxWidth: `calc(100vw - ${token.marginLG * 2}px)` },
      }}
    >
      <Button
        type="primary"
        loading={isCreating}
        disabled={isDisabled}
        icon={<Icon.ChevronsDown />}
      >
        {triggerLabel}
      </Button>
    </Dropdown>
  );

  return (
    <>
      <Modal
        title={
          <span>
            <ExclamationCircleOutlined
              style={{ color: token.colorError, marginRight: 8 }}
            />
            Archive form?
          </span>
        }
        open={showArchiveModal}
        onCancel={() => setShowArchiveModal(false)}
        onOk={handleArchiveConfirm}
        okText="Archive"
        okType="danger"
        cancelText="Cancel"
        confirmLoading={snap.isArchivingForm}
        centered
      >
        Form {formNumber || 'this form'} and all its versions will be moved to
        archive. You can restore it later from the Archive section.
      </Modal>
      <FormDerivedValuesDrawer
        open={derivedValuesOpen}
        onClose={() => setDerivedValuesOpen(false)}
      />
      {triggerControl}
    </>
  );
};

export default FormWorkflowSelect;
